// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>

import './styles.css';
import type { TowerData } from './types';
import { loadData } from './data';
import { initTooltip, hideTip } from './tooltip';
import { initGlossary, GLOSSARY, gloss, hideGloss } from './glossary';
import { closeSite } from './drawer';
import { esc, num } from './format';
import { trapFocus } from './views/ui';

import { mount as overview } from './views/overview';
import { mount as coverage } from './views/coverage';
import { mount as address } from './views/address';
import { mount as spectrum } from './views/spectrum';
import { mount as switchoff } from './views/switchoff';
import { mount as additionality } from './views/additionality';
import { mount as capture } from './views/capture';
import { mount as ignored } from './views/ignored';
import { mount as region } from './views/region';

type ViewFn = (el: HTMLElement, data: TowerData) => void | (() => void);
interface Tab { id: string; label: string; fn: ViewFn }

const TABS: Tab[] = [
  { id: 'overview', label: 'Overview', fn: overview },
  { id: 'coverage', label: 'Coverage', fn: coverage },
  { id: 'address', label: 'My Address', fn: address },
  { id: 'spectrum', label: 'Spectrum', fn: spectrum },
  { id: 'switch-off', label: 'Switch-Off', fn: switchoff },
  { id: 'additionality', label: 'Public Money', fn: additionality },
  { id: 'capture', label: 'Capture', fn: capture },
  { id: 'ignored', label: 'Targeting', fn: ignored },
  { id: 'region', label: 'Regions', fn: region },
];

let cleanup: (() => void) | void;
let data: TowerData;
let currentKey = '';

function parseHash(): { view: string; key: string } {
  const p = new URLSearchParams(location.hash.replace(/^#/, ''));
  const view = p.get('view') || 'overview';
  // Views that take a parameter re-render when it changes.
  return { view, key: `${view}|${p.get('sa3') ?? ''}|${p.get('state') ?? ''}` };
}

function renderShell(): void {
  const app = document.getElementById('app')!;
  app.innerHTML = `
    <a class="skip" href="#main">Skip to content</a>
    <header class="site-header">
      <div class="header-inner">
        <a class="brand" href="#view=overview">
          <svg class="brand-mark" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 21V9" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            <path d="M6 21l6-12 6 12" stroke="currentColor" stroke-width="2" stroke-linejoin="round" fill="none"/>
            <path d="M7.5 6.5a6 6 0 0 1 9 0M5 4a9.5 9.5 0 0 1 14 0" stroke="currentColor" stroke-width="1.6" fill="none" stroke-linecap="round"/>
          </svg>
          <span class="brand-txt"><strong>Mobile Towers</strong><small>who actually covers where you live</small></span>
        </a>
        <nav class="tabs" aria-label="Views">
          ${TABS.map((t) => `<a href="#view=${t.id}" data-tab="${t.id}">${t.label}</a>`).join('')}
        </nav>
        <button class="about-btn" data-about aria-label="About this site">?</button>
      </div>
    </header>
    <main class="main-content" id="main" data-main></main>
    <footer class="site-footer">
      <div class="footer-inner">
        <span>Sites: <a href="https://data.gov.au/data/dataset/accc-mobile-infrastructure-report-data-release" target="_blank" rel="noopener">ACCC Mobile Infrastructure Report</a> (CC BY 2.5 AU). Boundaries &amp; population: ABS (CC BY 4.0). Not affiliated with the ACCC, the ABS, or any carrier.</span>
        <span>Built by <a href="https://benrichardson.dev/">benrichardson.dev</a> · <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more tools &amp; sites</a></span>
      </div>
    </footer>`;

  app.querySelector('[data-about]')?.addEventListener('click', openAbout);
  window.addEventListener('hashchange', route);
}

function setActiveTab(view: string): void {
  document.querySelectorAll('[data-tab]').forEach((a) => {
    const on = a.getAttribute('data-tab') === view;
    a.classList.toggle('active', on);
    // The class alone is invisible to assistive technology, and on a narrow
    // screen the nav is a horizontal scroller in which the active tab can sit
    // off-screen entirely.
    if (on) {
      a.setAttribute('aria-current', 'page');
      (a as HTMLElement).scrollIntoView({ inline: 'center', block: 'nearest' });
    } else {
      a.removeAttribute('aria-current');
    }
  });
}

function route(): void {
  const { view, key } = parseHash();
  const tab = TABS.find((t) => t.id === view) || TABS[0];
  const main = document.querySelector('[data-main]') as HTMLElement;
  if (key === currentKey) return;
  currentKey = key;

  // Hygiene on every view change: drop any open panel, stale tooltip or popover.
  closeSite(false);
  hideTip();
  hideGloss();
  document.querySelector('.modal-host')?.remove();
  if (cleanup) { cleanup(); cleanup = undefined; }

  main.innerHTML = '';
  setActiveTab(tab.id);
  main.setAttribute('data-view', tab.id);
  try {
    cleanup = tab.fn(main, data) || undefined;
  } catch (err) {
    main.innerHTML = '<div class="error">Something went wrong rendering this view. Try another tab.</div>';
    // eslint-disable-next-line no-console
    console.error(err);
  }
  window.scrollTo({ top: 0 });
}

function openAbout(): void {
  const m = data.meta;
  const glossItems = Object.values(GLOSSARY).map((g) => `<dt>${esc(g.term)}</dt><dd>${esc(g.def)}</dd>`).join('');
  const host = document.createElement('div');
  host.className = 'modal-host';
  host.innerHTML = `
    <div class="modal-overlay" data-mclose></div>
    <div class="modal" role="dialog" aria-modal="true" aria-label="About this site">
      <div class="modal-head"><h2>About this site</h2><button data-mclose class="modal-close" aria-label="Close">✕</button></div>
      <div class="modal-body">
        <p><strong>Mobile Towers</strong> answers a question no carrier will answer for you: which mobile networks can actually reach a given place in Australia, what spectrum they use to do it, and which of those towers the public paid for.</p>

        <h3>Where the data comes from</h3>
        <p>Under the ACCC's <em>Audit of Telecommunications Infrastructure Assets</em> record-keeping rules, Telstra, Optus and TPG must report every mobile site they operate. The ACCC publishes those returns as open data: one file per carrier per year, ${m.years[0]}–${m.latestYear}, each row a site with its exact coordinates and a flag for every ${gloss('band', 'frequency band')} it runs. This site uses all ${m.years.length * 3 + 1} files — about 205,000 site-year rows.</p>
        <p>Region boundaries and populations are the ABS ${gloss('sa3', 'SA3')} geography and Estimated Resident Population (${m.erpYear}). Every site is placed in a region by point-in-polygon on its published coordinates.</p>

        <h3>How it updates</h3>
        <p>The ACCC publishes once a year. An automated pipeline re-downloads everything and rebuilds the site on that schedule, so it follows the official release. The current data is the ${m.latestYear} return, published ${esc(m.source.packageModified.slice(0, 10))}.</p>

        <h3>What counts as a "tower"</h3>
        <p>One carrier at one registered ${gloss('rfnsa', 'site number')} is one site. Several installations can share a registered number; where they do, their band lists are combined into one. That merged ${num(m.audit.mergedInstallations)} rows across all years. ${num(m.totals.sites)} sites stand in the ${m.latestYear} snapshot.</p>

        <h3>Things to be careful about</h3>
        <ul>
          <li><strong>This is a register, not a coverage map.</strong> It tells you a tower exists and what spectrum it runs. It does not model terrain, antenna direction, height or power, so a tower 5 km away may still not reach you if there is a hill in between.</li>
          <li><strong>A region is big.</strong> An SA3 can be well covered in its town and have nothing at all 30 km out. That is exactly why the <em>My Address</em> view measures from a point rather than colouring a region.</li>
          <li><strong>"A real option" is a judgement, and a stated one.</strong> Because nearly every SA3 contains all three carriers somewhere, a carrier is only counted as a genuine option where its long-reach site count reaches a third of the best-served carrier's. Raw counts are always on the hover.</li>
          <li><strong>Co-funding flags reflect the register, not a payment ledger.</strong> They mark sites the carriers report as having received a public contribution; the dollar amounts are not in this data.</li>
          <li><strong>Per-tower history starts in ${m.identityFrom}.</strong> The ${m.years[0]} and ${m.years[0] + 1} files carry no site number, so those years are used for counts only and never for tracking an individual tower.</li>
        </ul>

        <h3>What the pipeline had to clean up</h3>
        <ul>
          <li>${num(m.audit.rejectedYearRows)} rows in the ${m.identityFrom + 1} Telstra file carry impossible years (${m.audit.rejectedYearValues.slice(0, 4).join(', ')}…${m.audit.rejectedYearValues[m.audit.rejectedYearValues.length - 1]}) and were dropped.</li>
          <li>${m.audit.nonUtf8Files.length ? `${esc(m.audit.nonUtf8Files.join(', '))} is not UTF-8 and is decoded as Windows-1252.` : 'All files decoded as UTF-8.'}</li>
          <li>${num(m.audit.unplacedSites)} site could not be placed in any mainland region (external territories).</li>
        </ul>

        <h3>Glossary</h3>
        <dl class="gloss-list">${glossItems}</dl>

        <p class="muted">Mobile site data © Commonwealth of Australia (ACCC), <a href="${esc(m.source.licenceUrl)}" target="_blank" rel="noopener">${esc(m.source.licence)}</a>. Boundaries and population © Commonwealth of Australia (ABS), CC BY 4.0. Basemap © CARTO / © OpenStreetMap contributors. This site is not affiliated with the ACCC, the ABS, Telstra, Optus or TPG.</p>
      </div>
    </div>`;
  document.body.appendChild(host);
  const release = trapFocus(host.querySelector('.modal') as HTMLElement, document.getElementById('app'));
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') close();
  };
  function close(): void {
    document.removeEventListener('keydown', onKey);
    release();
    host.remove();
  }
  host.querySelectorAll('[data-mclose]').forEach((b) => b.addEventListener('click', close));
  document.addEventListener('keydown', onKey);
}

async function boot(): Promise<void> {
  renderShell();
  const main = document.querySelector('[data-main]') as HTMLElement;
  main.innerHTML = '<div class="loading"><span class="spin"></span> Loading every mobile tower in Australia…</div>';
  initTooltip();
  initGlossary();
  try {
    data = await loadData();
  } catch (err) {
    main.innerHTML = '<div class="error">Could not load the tower data. Please try again shortly.</div>';
    // eslint-disable-next-line no-console
    console.error(err);
    return;
  }
  route();
}

boot();
